import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginPage } from './pages/LoginPage';
import { NetworkPage } from './pages/NetworkPage';
import { ScreenPage } from './pages/ScreenPage';
import { AlertsPage } from './pages/AlertsPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import { StreamPage } from './pages/StreamPage';
import { StreamsPage } from './pages/StreamsPage';
import { UsersPage } from './pages/UsersPage';
import { VenueSetupPage } from './pages/VenueSetupPage';
import { VenuesPage } from './pages/VenuesPage';
import type { Permission } from './api/client';
import { VenuePage } from './pages/VenuePage';

function RequireAuth({ children }: { children: ReactNode }) {
  const { signedIn } = useAuth();
  const location = useLocation();
  if (!signedIn) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/** Sends users without the permission back to the map (the API refuses them anyway). */
function RequirePermission({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return null; // profile still loading
  if (!user.permissions.includes(permission)) return <Navigate to="/network" replace />;
  return children;
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/network" element={<RequireAuth><NetworkPage /></RequireAuth>} />
          <Route path="/venue/:venueId" element={<RequireAuth><VenuePage /></RequireAuth>} />
          <Route path="/screen/:screenId" element={<RequireAuth><ScreenPage /></RequireAuth>} />
          <Route path="/venues" element={<RequireAuth><RequirePermission permission="venues.manage"><VenuesPage /></RequirePermission></RequireAuth>} />
          <Route path="/venues/:venueId/setup" element={<RequireAuth><RequirePermission permission="venues.manage"><VenueSetupPage /></RequirePermission></RequireAuth>} />
          <Route path="/alerts" element={<RequireAuth><RequirePermission permission="network.view"><AlertsPage /></RequirePermission></RequireAuth>} />
          <Route path="/streams" element={<RequireAuth><RequirePermission permission="streams.manage"><StreamsPage /></RequirePermission></RequireAuth>} />
          <Route path="/streams/:streamId" element={<RequireAuth><RequirePermission permission="streams.manage"><StreamPage /></RequirePermission></RequireAuth>} />
          <Route path="/integrations" element={<RequireAuth><RequirePermission permission="integrations.manage"><IntegrationsPage /></RequirePermission></RequireAuth>} />
          <Route path="/users" element={<RequireAuth><RequirePermission permission="users.manage"><UsersPage /></RequirePermission></RequireAuth>} />
          <Route path="*" element={<Navigate to="/network" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
